import React, { useState, useEffect } from 'react';

import store, { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import {
  fetchProjectDetails,
  fetchProjectSettings,
  fetchWorkerProfileCustomFields,
  fetchWorkerProfileFieldsForAllCompanies,
  fetchCompanies,
  fetchConsentFormFields,
  fetchConsentAgreements,
  fetchCountries,
  fetchEthnicities,
  fetchLanguages,
  fetchStates,
  fetchIdTypes,
  fetchSkilledTrades,
  fetchJobTitles,
  fetchTradeStatus,
  fetchSocJobTitles,
  fetchLanguageTurnerProtocols,
  fetchCertificationTypes,
  setLastUpdatedAt,
} from 'modules/state-mgmt/cache';
import {
  fetchWorkerDetails,
  fetchAllCertifications,
  fetchCertificationGroups,
  fetchWorkerExistingConsentForms,
  fetchWorkerConsentAcceptances,
  fetchAssignedCompanies,
} from 'modules/state-mgmt/onboarding';
import { setIsFetchingData } from 'modules/state-mgmt/loader';
import { AuthService } from 'services/AuthService';

import { actionMapForOtherLoaders } from 'constants/actions';
import { ILoadingStatus } from 'models/general';

interface ILoadAllDataParams {
  projectId?: string;
  workerId?: string;
  employers?: string[];
  onlyImportantApis?: boolean;
  skipWorkerApis?: boolean;
  skipProjectApis?: boolean;
}

type UseLoadAllData = () => [
  {
    hasLoaded: boolean;
    isLoading: boolean;
    errors: any[];
  },
  (params?: ILoadAllDataParams) => Promise<void>,
];

const useLoadAllData: UseLoadAllData = () => {
  const [state, setState] = useState({
    isLoading: false,
    hasLoaded: false,
    errors: [],
  });

  const { hasLoaded, isLoading, errors } = state;

  // Capture skipWorkerApis in a ref to use in useEffect
  const skipWorkerApisRef = React.useRef<boolean>(false);

  const { loader, onboarding, languageCode, isOffline, isWorkerOrNoUser } = useAppSelector(state => ({
    loader: state.loader,
    onboarding: state.onboarding,
    languageCode: state.app?.languageCode,
    isOffline: !state.app?.isOnline,
    isWorkerOrNoUser: !state.user?.userId || state.user?.isWorker,
  }));

  const dispatch = useAppDispatch();

  const loadAllData = async ({
    projectId,
    workerId,
    employers,
    onlyImportantApis,
    skipWorkerApis,
    skipProjectApis,
  }: ILoadAllDataParams = {}) => {
    const pId = projectId || onboarding.temp?.project?.value;
    const wId = workerId || onboarding.id;
    const companyIds = employers?.length > 0 ? employers : onboarding.temp?.employers?.map?.(e => e.value) || [];

    // Store skipWorkerApis in ref for useEffect access
    skipWorkerApisRef.current = skipWorkerApis || false;

    // Offline Handling: Skip loading and mark as loaded
    if (isOffline || !navigator.onLine) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        hasLoaded: true,
        errors: [],
      }));
      return;
    }

    try {
      let session = await AuthService.getSession();
      const hasValidSession = !!session?.getIdToken?.()?.getJwtToken?.();

      if (skipProjectApis && hasValidSession) {
        // Only fetch reference data (no project-specific APIs)
        dispatch(setIsFetchingData(true));

        !skipWorkerApis && wId && dispatch(fetchWorkerDetails({ workerId: wId }));

        if (!onlyImportantApis) {
          dispatch(fetchEthnicities({ languageCode: 'en' }));
          dispatch(fetchEthnicities({ languageCode: 'es' }));

          dispatch(fetchLanguages({ languageCode: 'en' }));
          dispatch(fetchLanguages({ languageCode: 'es' }));

          dispatch(fetchCountries({ languageCode: 'en' }));
          dispatch(fetchCountries({ languageCode: 'es' }));

          dispatch(fetchStates({ languageCode: 'en' }));
          dispatch(fetchStates({ languageCode: 'es' }));

          dispatch(fetchIdTypes({ languageCode: 'en' }));
          dispatch(fetchIdTypes({ languageCode: 'es' }));

          dispatch(fetchSkilledTrades({ languageCode: 'en' }));
          dispatch(fetchSkilledTrades({ languageCode: 'es' }));

          dispatch(fetchCertificationTypes({ languageCode: 'en' }));
          dispatch(fetchCertificationTypes({ languageCode: 'es' }));

          dispatch(fetchJobTitles({ languageCode: 'en' }));
          dispatch(fetchJobTitles({ languageCode: 'es' }));

          dispatch(fetchTradeStatus({ languageCode: 'en' }));
          dispatch(fetchTradeStatus({ languageCode: 'es' }));

          dispatch(fetchSocJobTitles({ languageCode: 'en' }));
          dispatch(fetchSocJobTitles({ languageCode: 'es' }));

          dispatch(fetchLanguageTurnerProtocols({ languageCode: 'en' }));
          dispatch(fetchLanguageTurnerProtocols({ languageCode: 'es' }));
        }

        setState(prev => ({
          ...prev,
          isLoading: true,
          hasLoaded: false,
          errors: [],
        }));
      } else if (pId && companyIds.length > 0 && hasValidSession) {
        dispatch(setIsFetchingData(true));

        pId && dispatch(fetchProjectDetails({ projectId: pId }));

        pId && dispatch(fetchWorkerProfileFieldsForAllCompanies({ projectId: pId, languageCode: languageCode }));

        !skipWorkerApis && wId && dispatch(fetchWorkerDetails({ workerId: wId }));
        !skipWorkerApis &&
          wId &&
          pId &&
          dispatch(fetchAssignedCompanies({ projectId: pId, workerId: wId, languageCode: languageCode }));

        pId && dispatch(fetchCompanies({ projectId: pId, languageCode: 'en' }));
        pId && dispatch(fetchCompanies({ projectId: pId, languageCode: 'es' }));

        pId && dispatch(fetchProjectSettings({ projectId: pId }));

        const workerProfileCustomFieldsPayload: any = {
          projectId: pId,
        };
        if (workerId) {
          workerProfileCustomFieldsPayload.workerId = workerId;
          // In Case of worker login, fetch custom fields for all assigned companies
          if (companyIds?.length > 0) {
            workerProfileCustomFieldsPayload.companyIds = companyIds;
          }
        }
        pId && dispatch(fetchWorkerProfileCustomFields(workerProfileCustomFieldsPayload));

        //! Services.getWorkerProfileFieldsForAllCompanies(onboarding.project.id) and Services.getWorkerProfileCustomFields
        //! It is required for the offline flow. We will need to fetch worker profile fields for all companies and
        //! custom profile fields for that project and merge it manually in the offline mode.
        //! Store this data directly in the indexDB and only store the merged values in the cache

        if (!onlyImportantApis) {
          pId && dispatch(fetchConsentFormFields({ projectId: pId, languageCode: 'en' }));
          pId && dispatch(fetchConsentFormFields({ projectId: pId, languageCode: 'es' }));

          dispatch(fetchEthnicities({ languageCode: 'en' }));
          dispatch(fetchEthnicities({ languageCode: 'es' }));

          dispatch(fetchLanguages({ languageCode: 'en' }));
          dispatch(fetchLanguages({ languageCode: 'es' }));

          dispatch(fetchCountries({ languageCode: 'en' }));
          dispatch(fetchCountries({ languageCode: 'es' }));

          dispatch(fetchStates({ languageCode: 'en' }));
          dispatch(fetchStates({ languageCode: 'es' }));

          dispatch(fetchIdTypes({ languageCode: 'en' }));
          dispatch(fetchIdTypes({ languageCode: 'es' }));

          dispatch(fetchSkilledTrades({ languageCode: 'en' }));
          dispatch(fetchSkilledTrades({ languageCode: 'es' }));

          dispatch(fetchCertificationTypes({ languageCode: 'en' }));
          dispatch(fetchCertificationTypes({ languageCode: 'es' }));

          dispatch(fetchJobTitles({ languageCode: 'en' }));
          dispatch(fetchJobTitles({ languageCode: 'es' }));

          dispatch(fetchTradeStatus({ languageCode: 'en' }));
          dispatch(fetchTradeStatus({ languageCode: 'es' }));

          dispatch(fetchSocJobTitles({ languageCode: 'en' }));
          dispatch(fetchSocJobTitles({ languageCode: 'es' }));

          dispatch(fetchLanguageTurnerProtocols({ languageCode: 'en' }));
          dispatch(fetchLanguageTurnerProtocols({ languageCode: 'es' }));
        }

        if (!skipWorkerApis && pId && wId) {
          dispatch(fetchCertificationGroups({ projectId: pId, workerId: wId, languageCode }));
          dispatch(fetchAllCertifications({ workerId: wId, languageCode }));
          dispatch(fetchWorkerExistingConsentForms({ projectId: pId, workerId: wId, languageCode }));
        }

        // Project-level consent documents → cache slice. The API rejects this worker-agnostic call
        // on a worker token, so workers skip it and get their documents from the
        // worker-scoped call below instead.
        if (pId && !isWorkerOrNoUser) {
          dispatch(fetchConsentAgreements({ projectId: pId, languageCode }));
        }

        if (!skipWorkerApis && wId && pId) {
          dispatch(fetchWorkerConsentAcceptances({ projectId: pId, workerId: wId, languageCode }));
        }

        setState(prev => ({
          ...prev,
          isLoading: true,
          hasLoaded: false,
          errors: [],
        }));
      }
    } catch (error) {
      if (pId && companyIds?.length > 0) {
        setState(prev => ({
          ...prev,
          isLoading: true,
          hasLoaded: false,
          errors: [],
        }));
        const workerProfileCustomFieldsPayload: any = {
          projectId: pId,
        };
        if (workerId) {
          workerProfileCustomFieldsPayload.workerId = workerId;
          if (companyIds?.length > 0) {
            workerProfileCustomFieldsPayload.companyIds = companyIds;
          }
        }
        // No session here, so no token — use the public route.
        pId &&
          dispatch(
            fetchWorkerProfileFieldsForAllCompanies({ projectId: pId, languageCode: languageCode, isPublic: true }),
          );
        pId &&
          dispatch(fetchWorkerProfileCustomFields(workerProfileCustomFieldsPayload))
            .then(res => {
              //* No need to do anything since the useEffect will handle the state updates
            })
            .catch(error => {
              setState(prev => ({
                ...prev,
                isLoading: false,
                hasLoaded: false,
                errors: [error],
              }));
            });
      }
    }
  };

  const workerDetailsIsLoading = loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerDetails]?.isLoading;
  const allCertificationsIsLoading = loader.otherLoaders?.[actionMapForOtherLoaders.fetchAllCertifications]?.isLoading;
  const certificationGroupsIsLoading =
    loader.otherLoaders?.[actionMapForOtherLoaders.fetchCertificationGroups]?.isLoading;
  const workerConsentFormsIsLoading =
    loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerExistingConsentForms]?.isLoading;
  // Consent routing reads onboarding.acceptedStandardConsentIds, so this must settle before we
  // report hasLoaded — otherwise the worker can be routed back into the consent flow on arrival.
  const workerConsentAcceptancesIsLoading =
    loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerConsentAcceptances]?.isLoading;

  /**
   * Monitor async loading state and update local state when all loaders complete
   */
  useEffect(() => {
    const skipWorkerApis = skipWorkerApisRef.current;

    /**
     * Check if all reference data loaders have finished
     */
    const areReferenceLoadersComplete = () => {
      return !Object.values(loader.dataLoaders || {}).some((loader: ILoadingStatus) => loader.isLoading);
    };

    /**
     * Check if worker-specific loaders have finished (or skipped)
     */
    const areWorkerLoadersComplete = () => {
      // The consent-acceptance fetch runs even when worker APIs are skipped, so it is awaited in
      // both branches — consent routing reads its result.
      if (skipWorkerApis) return !workerConsentAcceptancesIsLoading;
      return !workerDetailsIsLoading && !workerConsentFormsIsLoading && !workerConsentAcceptancesIsLoading;
    };

    /**
     * Check if any loaders have errors
     */
    const hasAnyLoadingErrors = () => {
      // Check reference data loaders for errors
      const referenceDataHasErrors = Object.values(loader.dataLoaders || {}).some(
        (loader: ILoadingStatus) => loader.hasErrors,
      );

      if (skipWorkerApis) {
        return referenceDataHasErrors;
      }

      // Check worker-specific loaders for errors
      const workerLoadersWithErrors = [
        actionMapForOtherLoaders.fetchWorkerDetails,
        actionMapForOtherLoaders.fetchAllCertifications,
        actionMapForOtherLoaders.fetchCertificationGroups,
        actionMapForOtherLoaders.fetchWorkerExistingConsentForms,
        // On a worker session this call is the only source of the consent documents, so a silent
        // failure would leave the consent steps blank while still letting the worker click through.
        actionMapForOtherLoaders.fetchWorkerConsentAcceptances,
      ].some(actionKey => loader.otherLoaders?.[actionKey]?.hasErrors);

      return referenceDataHasErrors || workerLoadersWithErrors;
    };

    /**
     * Collect all errors from failed loaders
     */
    const collectLoadingErrors = () => {
      const allLoaders = {
        ...loader.dataLoaders,
        ...(!skipWorkerApis && {
          [actionMapForOtherLoaders.fetchWorkerDetails]:
            loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerDetails],
          [actionMapForOtherLoaders.fetchAllCertifications]:
            loader.otherLoaders?.[actionMapForOtherLoaders.fetchAllCertifications],
          [actionMapForOtherLoaders.fetchCertificationGroups]:
            loader.otherLoaders?.[actionMapForOtherLoaders.fetchCertificationGroups],
          [actionMapForOtherLoaders.fetchWorkerExistingConsentForms]:
            loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerExistingConsentForms],
          [actionMapForOtherLoaders.fetchWorkerConsentAcceptances]:
            loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerConsentAcceptances],
        }),
      };

      return Object.values(allLoaders)
        .filter((loader): loader is ILoadingStatus => loader?.hasErrors === true)
        .map(loader => loader.errors);
    };

    // Wait until all loaders are complete
    const allLoadersComplete = isLoading && areReferenceLoadersComplete() && areWorkerLoadersComplete();

    if (!allLoadersComplete) return;

    // Determine success or failure
    const hasDataLoaded = !hasAnyLoadingErrors();
    const loadingErrors = collectLoadingErrors();

    // Update timestamp only on successful load
    if (hasDataLoaded) {
      dispatch(setLastUpdatedAt());
    }

    // Update local state
    setState(prev => ({
      ...prev,
      isLoading: false,
      hasLoaded: hasDataLoaded,
      errors: loadingErrors,
    }));
  }, [
    isLoading,
    loader.dataLoaders,
    loader.otherLoaders,
    workerDetailsIsLoading,
    allCertificationsIsLoading,
    certificationGroupsIsLoading,
    workerConsentFormsIsLoading,
    workerConsentAcceptancesIsLoading,
    dispatch,
  ]);

  return [{ hasLoaded, isLoading, errors }, loadAllData];
};

export default useLoadAllData;
